import { makeObservable, observable, action } from "mobx";

// Models
import { LockerModel, ILockerModel } from "models/locker/entities/LockerModel";
import { LockerFeaturesModel, ILockerFeaturesModel } from "models/locker/entities/LockerFeaturesModel";
import { ModuleModel, IModuleModel } from "models/locker/entities/ModuleModel";
import { ContainerTypeModel, IContainerTypeModel } from "models/locker/entities/ContainerTypeModel";
import { ContainerMeasureModel, IContainerMeasureModel } from "models/locker/entities/ContainerMeasureModel";
import { ContainerModel, IContainerModel } from "models/locker/entities/ContainerModel";
import { AllocationModel } from "models/allocation/entities/AllocationModel";

// Services & Helpers
import StorageUtil, { KEYS } from "helpers/utils/StorageUtil";
import LockerAPI from "services/LockerAPI";
import LocalDBAPI from "services/LocalDBAPI";
import type { ErrorModel } from "models/shared/ErrorModel";

/**
 * Store para gerenciar o estado e as interações com um Locker específico.
 */
class LockerStore {
  // --- Estado Observável Específico ---
  loading: boolean = false;
  locker?: LockerModel;
  modules: ModuleModel[] = [];
  features?: LockerFeaturesModel;
  availableContainersTypes: ContainerTypeModel[] = [];
  availableContainersMeasures: ContainerMeasureModel[] = [];
  availableContainers: ContainerModel[] = [];

  constructor() {
    makeObservable(this, {
      // Herdado da BaseStore
      loading: observable,
      // Desta Store
      locker: observable,
      modules: observable,
      features: observable,
      availableContainersTypes: observable,
      availableContainersMeasures: observable,
      availableContainers: observable,
      reset: action,
      emptyLocker: action,
      get: action,
      getModules: action,
      setLockerKeys: action,
      clearLockerKeys: action,
      getFeatures: action,
      getAvailableContainerTypes: action,
      getAvailableContainerMeasures: action,
      getAvailableContainersByTypeAndMeasure: action,
      onlineLockerWebhook: action,
      authTokenLocalDB: action,
      logTotalUnavailability: action,
      logUnavailableContainerMeasure: action,
      logEndSession: action,
      logUnblockContainerByFailure: action,
      logSupplyContainerFailure: action,
      logSupplyFailure: action,
      logRetrieveFailure: action,
      logInactivityDetected: action,
    });
  }

  /** Implementação do método abstrato reset. */
  reset(): void {
    this.locker = undefined;
    this.features = undefined;
    this.modules = [];
    this.availableContainersTypes = [];
    this.availableContainersMeasures = [];
    this.availableContainers = [];
    this.loading = false;
  }

  /** Cria instâncias vazias de locker e features. */
  emptyLocker(): void {
    this.locker = undefined;
    this.features = undefined;
  }

  /** Salva as chaves do locker no storage local. */
  setLockerKeys(uuid: string, company_uuid: string, token: string): void {
    StorageUtil.setItem(KEYS.LOCKER_UUID, uuid);
    StorageUtil.setItem(KEYS.COMPANY_UUID, company_uuid);
    StorageUtil.setItem(KEYS.LOCKER_TOKEN, token);
  }

  /** Limpa as chaves do locker do storage. */
  clearLockerKeys(): void {
    console.log('Limpando chaves do locker...');
    // StorageUtil.remove(KEYS.LOCKER_UUID);
    // StorageUtil.remove(KEYS.LOCKER_TOKEN);
  }

  // --- Ações Assíncronas (Interação com APIs) ---

  /** Busca os dados de um locker pelo UUID. */
  async get(uuid: string): Promise<LockerModel | undefined> {
    this.loading = true;
    try {
      const response = await LockerAPI.get(uuid);
      if (!response.error) {
        this.locker = new LockerModel(response);
        return this.locker;
      } else if (!response.isNetworkError) {
        this.clearLockerKeys();
      }
    } catch (error) {
      console.error("Falha ao buscar locker:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Busca os módulos de um locker. */
  async getModules(uuid: string): Promise<ModuleModel[] | undefined> {
    this.loading = true;
    try {
      const response = await LockerAPI.getModules(uuid);
      if (!response.error) {
        this.modules = response.map((i: IModuleModel) => new ModuleModel(i));
        return this.modules;
      } else if (!response.isNetworkError) {
        this.clearLockerKeys();
      }
    } catch (error) {
      console.error("Falha ao buscar módulos:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Busca as features de um locker. */
  async getFeatures(uuid?: string): Promise<LockerFeaturesModel | ErrorModel | undefined> {
    this.loading = true;
    const lockerUuid = uuid || StorageUtil.getItem(KEYS.LOCKER_UUID);
    try {
      if (!lockerUuid) throw new Error("UUID do Locker não encontrado.");
      const response = await LockerAPI.getFeatures(lockerUuid);
      if (!response.error) {
        this.features = new LockerFeaturesModel(response);
        return this.features;
      } else if (!response.isNetworkError) {
        this.clearLockerKeys();
      }
      return response;
    } catch (error) {
      console.error("Falha ao buscar features:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Busca os tipos de compartimentos disponíveis. */
  async getAvailableContainerTypes(): Promise<ContainerTypeModel[] | undefined> {
    this.loading = true;
    const uuid = StorageUtil.getItem(KEYS.LOCKER_UUID);
    try {
      if (!uuid) throw new Error("UUID do Locker não encontrado.");
      const response = await LockerAPI.getAvailableContainerTypes(uuid);
      if (!response.error) {
        this.availableContainersTypes = response.content.map(
          (i: IContainerTypeModel) => new ContainerTypeModel(i)
        );
        return this.availableContainersTypes;
      }
    } catch (error) {
      console.error("Falha ao buscar tipos de compartimentos:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Busca as medidas de compartimentos disponíveis para um tipo específico. */
  async getAvailableContainerMeasures(uuidContainerType: string): Promise<ContainerMeasureModel[] | undefined> {
    this.loading = true;
    const uuid = StorageUtil.getItem(KEYS.LOCKER_UUID);
    try {
      if (!uuid) throw new Error("UUID do Locker não encontrado.");
      const response = await LockerAPI.getAvailableContainerMeasures(uuid, uuidContainerType);
      if (!response.error) {
        this.availableContainersMeasures = response.content.map(
          (i: IContainerMeasureModel) => new ContainerMeasureModel(i)
        );
        return this.availableContainersMeasures;
      }
    } catch (error) {
      console.error("Falha ao buscar medidas de compartimentos:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Busca compartimentos disponíveis por tipo e medida. */
  async getAvailableContainersByTypeAndMeasure(
    uuidContainerType: string,
    uuidContainerMeasure: string
  ): Promise<ContainerModel[] | undefined> {
    this.loading = true;
    const uuid = StorageUtil.getItem(KEYS.LOCKER_UUID);
    try {
      if (!uuid) throw new Error("UUID do Locker não encontrado.");
      const response = await LockerAPI.getAvailableContainersByTypeAndMeasure(
        uuid,
        uuidContainerType,
        uuidContainerMeasure
      );
      if (!response.error) {
        this.availableContainers = response.content.map(
          (i: IContainerModel) => new ContainerModel(i)
        );
        return this.availableContainers;
      }
    } catch (error) {
      console.error("Falha ao buscar compartimentos por tipo e medida:", error);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  /** Envia um webhook indicando que o locker está online. */
  async onlineLockerWebhook(): Promise<any> {
    const uuidLocker = StorageUtil.getItem(KEYS.LOCKER_UUID);
    if (!uuidLocker) return false;
    try {
      return await LockerAPI.onlineLockerWebhook(uuidLocker);
    } catch (error) {
      console.error("Falha ao enviar webhook online:", error);
    }
  }

  /** Autentica o token do locker com o banco de dados local. */
  async authTokenLocalDB(): Promise<void> {
    await LocalDBAPI.authToken();
  }

  // --- Ações de Logging Local ---
  async logTotalUnavailability(): Promise<void> {
    LocalDBAPI.totalUnavailability();
  }

  async logUnavailableContainerMeasure(): Promise<void> {
    LocalDBAPI.sizeUnavailability();
  }

  async logEndSession(): Promise<void> {
    LocalDBAPI.endSession();
  }

  async logUnblockContainerByFailure(container: ContainerModel, allocation: AllocationModel): Promise<void> {
    LocalDBAPI.unblockCompartmentsByFailure(container.uuid, allocation.uuid!);
  }

  async logSupplyContainerFailure(container: ContainerModel, allocation: AllocationModel): Promise<void> {
    LocalDBAPI.supplyCompartmentFailure(container.uuid, allocation.uuid!);
  }

  async logSupplyFailure(allocation: AllocationModel): Promise<void> {
    LocalDBAPI.supplyFailure(allocation.uuid!);
  }

  async logRetrieveFailure(allocation: AllocationModel): Promise<void> {
    LocalDBAPI.retrieveFailure(allocation.uuid!);
  }

  async logInactivityDetected(container?: ContainerModel, allocation?: AllocationModel,): Promise<void> {
    LocalDBAPI.logInactivityDetected(container?.uuid, allocation?.uuid,);
  }
}

export default LockerStore;